import { z } from 'zod';
import type { Occurrence } from './occurrences.js';
import type { StatusLookup } from './progress.js';
import { itemColorSchema, localDateSchema, localTimeSchema, prioritySchema } from './schemas.js';
import {
  addDays,
  dateRange,
  minutesToTime,
  startOfWeek,
  timeToMinutes,
  weekdayOf,
  type LocalDate,
  type LocalTime,
} from './time.js';

// ---------------------------------------------------------------------------------------------
// Day templates, week patterns (week A/B) and periods

const nameSchema = z.string().trim().min(1, 'Ime je obvezno.').max(60);

/** A typical day ("Šolski dan", "Prost dan"). Its blocks are items with `templateId`. */
export const dayTemplateInputSchema = z
  .object({ name: nameSchema, color: itemColorSchema.default('blue') })
  .strict();
export type DayTemplateInput = z.input<typeof dayTemplateInputSchema>;
export const dayTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: itemColorSchema,
});
export type DayTemplate = z.infer<typeof dayTemplateSchema>;

/**
 * Which template each weekday uses. `days[week * 7 + weekday - 1]`; a two-week cycle alternates
 * week A and week B, counted from the Monday `anchor`.
 */
export const weekPatternInputSchema = z
  .object({
    name: nameSchema,
    cycleWeeks: z.union([z.literal(1), z.literal(2)]).default(1),
    anchor: localDateSchema.refine((d) => weekdayOf(d) === 1, 'Začetek mora biti ponedeljek.'),
    days: z.array(z.uuid().nullable()).max(14),
  })
  .strict()
  .refine((p) => p.days.length === p.cycleWeeks * 7, {
    message: 'Vzorec mora imeti 7 dni na teden.',
    path: ['days'],
  });
export type WeekPatternInput = z.input<typeof weekPatternInputSchema>;
export const weekPatternSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  cycleWeeks: z.union([z.literal(1), z.literal(2)]),
  anchor: localDateSchema,
  days: z.array(z.uuid().nullable()),
});
export type WeekPattern = z.infer<typeof weekPatternSchema>;

/** A period with its own pattern ("Izpitno obdobje", "Počitnice"); null pattern = no templates. */
export const planPeriodInputSchema = z
  .object({
    name: nameSchema,
    startDate: localDateSchema,
    endDate: localDateSchema,
    patternId: z.uuid().nullable(),
  })
  .strict()
  .refine((p) => p.endDate >= p.startDate, {
    message: 'Konec je pred začetkom.',
    path: ['endDate'],
  });
export type PlanPeriodInput = z.input<typeof planPeriodInputSchema>;
export const planPeriodSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  startDate: localDateSchema,
  endDate: localDateSchema,
  patternId: z.uuid().nullable(),
});
export type PlanPeriod = z.infer<typeof planPeriodSchema>;

/** "Ta dan je drugačen": a single day uses another template (or none). */
export const dayOverrideSchema = z.object({
  date: localDateSchema,
  templateId: z.uuid().nullable(),
});
export type DayOverride = z.infer<typeof dayOverrideSchema>;

/**
 * When you get up and go to bed. `bed` earlier than `wake` means after midnight (e.g. 00:30).
 * Outside this time the calendar is shaded and nothing is planned.
 */
export const sleepTimesSchema = z
  .object({ wake: localTimeSchema, bed: localTimeSchema })
  .strict()
  .refine((s) => s.wake !== s.bed, { message: 'Vstajanje in spanje ne moreta biti ob isti uri.' });
export type SleepTimes = z.infer<typeof sleepTimesSchema>;

/** Sleep for every day of the week, Monday first. `bed` = going to bed that evening. */
export const weekSleepSchema = z
  .object({ days: z.array(sleepTimesSchema).length(7, 'Za vsak dan v tednu.') })
  .strict();
export type WeekSleep = z.infer<typeof weekSleepSchema>;

/** Before sleep per day: one setting for Mon–Fri and one for Sat–Sun. */
interface LegacyWeekSleep {
  weekday: SleepTimes;
  weekend: SleepTimes;
}
const isLegacySleep = (v: unknown): v is LegacyWeekSleep =>
  typeof v === 'object' && v !== null && 'weekday' in v && 'weekend' in v && !('days' in v);

/** Old "weekday / weekend" sleep becomes the same times on each of those days. */
export function upgradeWeekSleep(v: unknown): unknown {
  if (!isLegacySleep(v)) return v;
  const { weekday, weekend } = v;
  return { days: [weekday, weekday, weekday, weekday, weekday, weekend, weekend] };
}

export const planningSettingsSchema = z
  .object({
    activePatternId: z.uuid().nullable(),
    // Older apps (and data saved before) send { weekday, weekend }: accepted and converted.
    sleep: z.preprocess(upgradeWeekSleep, weekSleepSchema),
  })
  .strict();
export type PlanningSettings = z.infer<typeof planningSettingsSchema>;
export const DEFAULT_PLANNING_SETTINGS: PlanningSettings = {
  activePatternId: null,
  sleep: {
    days: [
      { wake: '07:00', bed: '23:00' },
      { wake: '07:00', bed: '23:00' },
      { wake: '07:00', bed: '23:00' },
      { wake: '07:00', bed: '23:00' },
      { wake: '07:00', bed: '00:00' },
      { wake: '09:00', bed: '00:00' },
      { wake: '09:00', bed: '23:00' },
    ],
  },
};

/** Full Slovene day names, Monday first (index = weekday − 1). */
export const WEEKDAY_NAMES_SL = [
  'Ponedeljek',
  'Torek',
  'Sreda',
  'Četrtek',
  'Petek',
  'Sobota',
  'Nedelja',
] as const;

/** Different sleep on one day ("jutri vstajam ob 5:00"). */
export const daySleepSchema = z.object({ date: localDateSchema }).extend(sleepTimesSchema.shape);
export type DaySleep = z.infer<typeof daySleepSchema>;

/** Own all-day / multi-day events: holidays, camping, a trip. */
export const allDayInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Ime je obvezno.').max(120),
    color: itemColorSchema.default('teal'),
    startDate: localDateSchema,
    endDate: localDateSchema,
    /** "Ne planiraj teh dni": no day templates and repeating items (unless kept), no reminders. */
    pausePlan: z.boolean().default(true),
    notes: z.string().trim().max(2000).nullable().default(null),
  })
  .strict()
  .refine((e) => e.endDate >= e.startDate, {
    message: 'Konec je pred začetkom.',
    path: ['endDate'],
  })
  .refine((e) => Date.parse(e.endDate) - Date.parse(e.startDate) <= 366 * 86_400_000, {
    message: 'Največ eno leto.',
    path: ['endDate'],
  });
export type AllDayInput = z.input<typeof allDayInputSchema>;
export const allDaySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  color: itemColorSchema,
  startDate: localDateSchema,
  endDate: localDateSchema,
  pausePlan: z.boolean(),
  notes: z.string().nullable(),
});
export type AllDayItem = z.infer<typeof allDaySchema>;

export interface PlanContext {
  templates: readonly DayTemplate[];
  patterns: readonly WeekPattern[];
  periods: readonly PlanPeriod[];
  overrides: readonly DayOverride[];
  settings: PlanningSettings;
  allDay: readonly AllDayItem[];
  sleepOverrides: readonly DaySleep[];
}

export const EMPTY_PLAN: PlanContext = {
  templates: [],
  patterns: [],
  periods: [],
  overrides: [],
  settings: DEFAULT_PLANNING_SETTINGS,
  allDay: [],
  sleepOverrides: [],
};

/**
 * Fills in what an older server (or data cached before an update) does not send yet, so the
 * client never crashes on a missing list or setting.
 */
export function withPlanDefaults<T extends Partial<PlanContext>>(plan: T): T & PlanContext {
  const settings = (plan.settings ?? {}) as Partial<PlanningSettings>;
  return {
    ...plan,
    templates: plan.templates ?? [],
    patterns: plan.patterns ?? [],
    periods: plan.periods ?? [],
    overrides: plan.overrides ?? [],
    allDay: plan.allDay ?? [],
    sleepOverrides: plan.sleepOverrides ?? [],
    settings: {
      activePatternId: settings.activePatternId ?? null,
      sleep: (() => {
        // Older server: { weekday, weekend }; missing or broken: defaults.
        const parsed = weekSleepSchema.safeParse(upgradeWeekSleep(settings.sleep));
        return parsed.success ? parsed.data : DEFAULT_PLANNING_SETTINGS.sleep;
      })(),
    },
  };
}

/** Own all-day events on a date. */
export const allDayOn = (plan: Pick<PlanContext, 'allDay'>, date: LocalDate) =>
  plan.allDay.filter((e) => e.startDate <= date && date <= e.endDate);

/** The all-day event that pauses planning on `date` (holiday), if any. */
export const pauseOn = (plan: Pick<PlanContext, 'allDay'>, date: LocalDate) =>
  allDayOn(plan, date).find((e) => e.pausePlan) ?? null;

export interface ResolvedSleep extends SleepTimes {
  /** `day` = set for this date only; `week` = the usual times of this day of the week. */
  source: 'day' | 'week';
}

/** Sleep times of a date: that day's own, otherwise the usual ones of its day of the week. */
export function sleepFor(
  plan: Pick<PlanContext, 'settings' | 'sleepOverrides'>,
  date: LocalDate,
): ResolvedSleep {
  const own = plan.sleepOverrides.find((o) => o.date === date);
  if (own) return { wake: own.wake, bed: own.bed, source: 'day' };
  const s =
    plan.settings.sleep.days[weekdayOf(date) - 1] ?? DEFAULT_PLANNING_SETTINGS.sleep.days[0];
  return { wake: s?.wake ?? '07:00', bed: s?.bed ?? '23:00', source: 'week' };
}

/** Minutes of sleep from going to bed at `bed` to getting up at `wake` (the next morning). */
export function sleepLength(bed: string, wake: string): number {
  const minutes = (timeToMinutes(wake) - timeToMinutes(bed) + 24 * 60) % (24 * 60);
  return minutes === 0 ? 24 * 60 : minutes;
}

/** Awake part of the day in minutes; going to bed after midnight counts as the end of the day. */
export function awakeWindow(sleep: SleepTimes): { start: number; end: number } {
  const start = timeToMinutes(sleep.wake);
  const bed = timeToMinutes(sleep.bed);
  return { start, end: bed > start ? bed : 24 * 60 };
}

/** Where free time may be planned on a date (null on a paused day such as a holiday). */
export const planWindow =
  (plan: PlanContext) =>
  (date: LocalDate): { start: number; end: number } | null =>
    pauseOn(plan, date) ? null : awakeWindow(sleepFor(plan, date));

export interface ResolvedDay {
  templateId: string | null;
  /** Why this template applies; `pause` = an all-day event stops planning that day. */
  source: 'override' | 'period' | 'pattern' | 'none' | 'pause';
  period: PlanPeriod | null;
  pattern: WeekPattern | null;
  /** 0 = week A, 1 = week B (only for two-week patterns). */
  weekIndex: number;
  /** Holiday or similar that pauses the plan. */
  pause: AllDayItem | null;
}

/** 0 for week A, 1 for week B of a two-week cycle. */
export function patternWeekIndex(
  pattern: Pick<WeekPattern, 'cycleWeeks' | 'anchor'>,
  date: LocalDate,
) {
  if (pattern.cycleWeeks === 1) return 0;
  const weeks = Math.round(
    (Date.parse(startOfWeek(date)) - Date.parse(startOfWeek(pattern.anchor))) / (7 * 86_400_000),
  );
  return ((weeks % pattern.cycleWeeks) + pattern.cycleWeeks) % pattern.cycleWeeks;
}

/** Which day template applies on `date`: holiday (none) > day override > period > pattern. */
export function resolveDay(plan: PlanContext, date: LocalDate): ResolvedDay {
  const known = (id: string | null | undefined) =>
    id != null && plan.templates.some((t) => t.id === id) ? id : null;
  const pause = pauseOn(plan, date);
  const none = { period: null, pattern: null, weekIndex: 0, pause: null };

  const override = plan.overrides.find((o) => o.date === date);
  // Periods are listed newest-first by the API; the shortest matching one wins (most specific).
  const period =
    [...plan.periods]
      .filter((p) => p.startDate <= date && date <= p.endDate)
      .sort(
        (a, b) =>
          Date.parse(a.endDate) -
          Date.parse(a.startDate) -
          (Date.parse(b.endDate) - Date.parse(b.startDate)),
      )[0] ?? null;
  const patternId = period ? period.patternId : plan.settings.activePatternId;
  const pattern = plan.patterns.find((p) => p.id === patternId) ?? null;
  const weekIndex = pattern ? patternWeekIndex(pattern, date) : 0;

  if (pause)
    return { ...none, period, pattern, weekIndex, pause, templateId: null, source: 'pause' };
  if (override) {
    return {
      ...none,
      period,
      pattern,
      weekIndex,
      templateId: known(override.templateId),
      source: 'override',
    };
  }
  if (!pattern) return { ...none, period, templateId: null, source: period ? 'period' : 'none' };
  const templateId = known(pattern.days[weekIndex * 7 + weekdayOf(date) - 1]);
  return {
    templateId,
    source: period ? 'period' : 'pattern',
    period,
    pattern,
    weekIndex,
    pause: null,
  };
}

/** Dates in [from, to] on which `templateId` applies. */
export function templateDates(
  plan: PlanContext,
  templateId: string,
  from: LocalDate,
  to: LocalDate,
): LocalDate[] {
  return dateRange(from, to).filter((d) => resolveDay(plan, d).templateId === templateId);
}

// ---------------------------------------------------------------------------------------------
// Weekly goals

export const goalInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Ime je obvezno.').max(120),
    color: itemColorSchema.default('green'),
    /** Sessions per week. */
    targetPerWeek: z.number().int().min(1).max(21),
    /** Length of one session (used for suggestions). */
    sessionMin: z.number().int().min(5).max(480).default(60),
    /** Preferred time of day for suggestions. */
    windowStart: localTimeSchema.nullable().default(null),
    windowEnd: localTimeSchema.nullable().default(null),
  })
  .strict()
  .refine((g) => !g.windowStart || !g.windowEnd || g.windowEnd > g.windowStart, {
    message: 'Okno se konča pred začetkom.',
    path: ['windowEnd'],
  });
export type GoalInput = z.input<typeof goalInputSchema>;
export const goalSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  color: itemColorSchema,
  targetPerWeek: z.number().int(),
  sessionMin: z.number().int(),
  windowStart: localTimeSchema.nullable(),
  windowEnd: localTimeSchema.nullable(),
});
export type Goal = z.infer<typeof goalSchema>;

export interface GoalProgress {
  target: number;
  /** Sessions in the week that are not skipped/missed (planned or done). */
  planned: number;
  done: number;
  /** Sessions still needed in the plan to reach the target. */
  missing: number;
}

/** Progress of a goal in one week, from the occurrences of that week. */
export function goalProgress(
  goal: Pick<Goal, 'id' | 'targetPerWeek'>,
  weekOccurrences: readonly Occurrence[],
  status: StatusLookup,
): GoalProgress {
  let planned = 0;
  let done = 0;
  for (const o of weekOccurrences) {
    if (o.goalId !== goal.id) continue;
    const s = status(o.key);
    if (s === 'done') done += 1;
    if (s !== 'missed' && s !== 'skipped') planned += 1;
  }
  return {
    target: goal.targetPerWeek,
    planned,
    done,
    missing: Math.max(0, goal.targetPerWeek - planned),
  };
}

// ---------------------------------------------------------------------------------------------
// Inbox tasks

export const taskInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Ime je obvezno.').max(120),
    notes: z.string().trim().max(2000).nullable().default(null),
    /** Estimate, used as the length of the time box. */
    durationMin: z.number().int().min(5).max(480).default(30),
    priority: prioritySchema.default('flex'),
    color: itemColorSchema.nullable().default(null),
    dueDate: localDateSchema.nullable().default(null),
  })
  .strict();
export type TaskInput = z.input<typeof taskInputSchema>;
export const taskPatchSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    notes: z.string().trim().max(2000).nullable(),
    durationMin: z.number().int().min(5).max(480),
    priority: prioritySchema,
    color: itemColorSchema.nullable(),
    dueDate: localDateSchema.nullable(),
    /** Mark done/undone without scheduling. */
    done: z.boolean(),
  })
  .partial()
  .strict();
export type TaskPatch = z.infer<typeof taskPatchSchema>;
export const taskSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  notes: z.string().nullable(),
  durationMin: z.number().int(),
  priority: prioritySchema,
  color: itemColorSchema.nullable(),
  dueDate: localDateSchema.nullable(),
  /** Set when done (directly, or its time box was checked off). */
  doneAt: z.string().nullable(),
  /** Date of the latest time box, if any. */
  scheduledFor: localDateSchema.nullable(),
  scheduledItemId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type Task = z.infer<typeof taskSchema>;

export const scheduleTaskSchema = z
  .object({
    date: localDateSchema,
    startTime: localTimeSchema,
    durationMin: z
      .number()
      .int()
      .min(5)
      .max(24 * 60)
      .optional(),
  })
  .strict();
export type ScheduleTaskInput = z.infer<typeof scheduleTaskSchema>;

export type TaskState = 'inbox' | 'overdue' | 'scheduled' | 'done';

/** Where a task belongs: waiting in the inbox, its time box passed undone, planned, or done. */
export function taskState(
  task: Pick<Task, 'doneAt' | 'scheduledFor'>,
  today: LocalDate,
): TaskState {
  if (task.doneAt) return 'done';
  if (!task.scheduledFor) return 'inbox';
  return task.scheduledFor < today ? 'overdue' : 'scheduled';
}

// ---------------------------------------------------------------------------------------------
// Free time, conflicts and suggestions

/** Minutes since midnight, end exclusive. */
export interface Interval {
  start: number;
  end: number;
}

export const SLOT_STEP_MIN = 15;

type BusySource = Pick<Occurrence, 'startTime' | 'durationMin'> &
  Partial<Pick<Occurrence, 'travelBeforeMin' | 'travelAfterMin'>>;

/** Busy time of one occurrence, including the travel there and back. */
const occInterval = (o: BusySource): Interval => {
  const start = timeToMinutes(o.startTime);
  return {
    start: Math.max(0, start - (o.travelBeforeMin ?? 0)),
    end: Math.min(24 * 60, start + o.durationMin + (o.travelAfterMin ?? 0)),
  };
};

/**
 * Merged busy time of one day. Zero-length items (reminders) do not block anything, unless
 * there is travel to them (an appointment "ob 14:30" with 20 min of driving).
 */
export function busyIntervals(occurrences: readonly BusySource[]) {
  const list = occurrences
    .filter((o) => o.durationMin > 0 || (o.travelBeforeMin ?? 0) + (o.travelAfterMin ?? 0) > 0)
    .map(occInterval)
    .sort((a, b) => a.start - b.start);
  const merged: Interval[] = [];
  for (const i of list) {
    const last = merged.at(-1);
    if (last && i.start <= last.end) last.end = Math.max(last.end, i.end);
    else merged.push({ ...i });
  }
  return merged;
}

/**
 * Start times (minutes) where `duration` fits between `from` and `to` without touching busy time:
 * the earliest aligned start in every gap.
 */
export function freeSlots(input: {
  busy: readonly Interval[];
  duration: number;
  from: number;
  to: number;
  step?: number;
}): number[] {
  const step = input.step ?? SLOT_STEP_MIN;
  const align = (m: number) => Math.ceil(m / step) * step;
  const out: number[] = [];
  let cursor = align(input.from);
  for (const b of [...input.busy, { start: input.to, end: input.to }]) {
    const gapEnd = Math.min(b.start, input.to);
    if (cursor + input.duration <= gapEnd) out.push(cursor);
    cursor = Math.max(cursor, align(b.end));
    if (cursor >= input.to) break;
  }
  return out;
}

/** Occurrences that overlap a planned time on the same day (zero-length ones are ignored). */
export function overlapping<T extends Pick<Occurrence, 'key' | 'startTime' | 'durationMin'>>(
  candidate: {
    startTime: LocalTime;
    durationMin: number;
    ignoreKey?: string;
    travelBeforeMin?: number;
    travelAfterMin?: number;
  },
  sameDay: readonly T[],
): T[] {
  if (candidate.durationMin <= 0) return [];
  const c = occInterval(candidate);
  return sameDay.filter((o) => {
    if (o.key === candidate.ignoreKey || o.durationMin <= 0) return false;
    const i = occInterval(o);
    return i.start < c.end && c.start < i.end;
  });
}

/** Keys of occurrences that overlap another one on the same day (for conflict warnings). */
export function conflictKeys(dayOccurrences: readonly Occurrence[]): Set<string> {
  const timed = dayOccurrences.filter((o) => o.durationMin > 0);
  const out = new Set<string>();
  for (const o of timed) {
    if (overlapping({ ...o, ignoreKey: o.key }, timed).length > 0) out.add(o.key);
  }
  return out;
}

export interface Slot {
  date: LocalDate;
  startTime: LocalTime;
  durationMin: number;
}

export interface SlotSearch {
  /** Occurrences per date (own items and timed calendar events). */
  byDate: ReadonlyMap<LocalDate, readonly Occurrence[]>;
  /** Awake time of a date in minutes (see `planWindow`); null = do not plan that day. */
  window: (date: LocalDate) => Interval | null;
  /** Today and the current time: nothing is suggested in the past. */
  today: LocalDate;
  nowTime: LocalTime;
}

function dayWindow(
  date: LocalDate,
  search: SlotSearch,
  window?: { start: LocalTime | null; end: LocalTime | null },
): Interval | null {
  const day = search.window(date);
  if (!day || date < search.today) return null;
  // A preferred window (e.g. a goal "zvečer") is narrowed to the awake time.
  let from = Math.max(day.start, window?.start ? timeToMinutes(window.start) : 0);
  const to = Math.min(day.end, window?.end ? timeToMinutes(window.end) : 24 * 60);
  if (date === search.today) from = Math.max(from, timeToMinutes(search.nowTime) + 5);
  return from < to ? { start: from, end: to } : null;
}

/** First time on `date` where `durationMin` fits (optionally not before `after`). */
export function firstFreeSlot(
  date: LocalDate,
  durationMin: number,
  search: SlotSearch,
  options: {
    after?: LocalTime;
    ignoreKey?: string;
    window?: { start: LocalTime | null; end: LocalTime | null };
  } = {},
): Slot | null {
  const w = dayWindow(date, search, options.window);
  if (!w) return null;
  const from = Math.max(w.start, options.after ? timeToMinutes(options.after) : 0);
  const occs = (search.byDate.get(date) ?? []).filter((o) => o.key !== options.ignoreKey);
  const [start] = freeSlots({ busy: busyIntervals(occs), duration: durationMin, from, to: w.end });
  return start === undefined ? null : { date, startTime: minutesToTime(start), durationMin };
}

/** First free slot from `date` on, looking up to `days` days ahead. */
export function nextFreeSlot(
  date: LocalDate,
  durationMin: number,
  search: SlotSearch,
  options: { after?: LocalTime; ignoreKey?: string; days?: number } = {},
): Slot | null {
  for (let i = 0; i < (options.days ?? 7); i += 1) {
    const d = addDays(date, i);
    const slot = firstFreeSlot(d, durationMin, search, {
      ignoreKey: options.ignoreKey,
      after: i === 0 ? options.after : undefined,
    });
    if (slot) return slot;
  }
  return null;
}

/**
 * Suggests time slots for the missing sessions of a goal in a week: one per day, spread out as
 * evenly as possible, inside the goal's preferred window, avoiding days that already have one.
 */
export function suggestGoalSlots(
  goal: Pick<Goal, 'id' | 'sessionMin' | 'windowStart' | 'windowEnd'>,
  week: readonly LocalDate[],
  count: number,
  search: SlotSearch,
): Slot[] {
  if (count <= 0) return [];
  const window = { start: goal.windowStart, end: goal.windowEnd };
  const taken = week.filter((d) => (search.byDate.get(d) ?? []).some((o) => o.goalId === goal.id));
  const candidates = week
    .filter((d) => !taken.includes(d))
    .map((d) => firstFreeSlot(d, goal.sessionMin, search, { window }))
    .filter((s): s is Slot => s !== null);

  const index = (d: LocalDate) => week.indexOf(d);
  const chosen: Slot[] = [];
  const anchors = taken.map(index);
  while (chosen.length < count && candidates.length > 0) {
    // Pick the candidate furthest from every day that already has a session.
    let best = 0;
    let bestScore = -1;
    candidates.forEach((c, i) => {
      const pos = index(c.date);
      const score =
        anchors.length === 0 ? -pos : Math.min(...anchors.map((a) => Math.abs(a - pos)));
      if (score > bestScore) {
        best = i;
        bestScore = score;
      }
    });
    const [pick] = candidates.splice(best, 1);
    if (!pick) break;
    chosen.push(pick);
    anchors.push(index(pick.date));
  }
  return chosen.sort((a, b) => a.date.localeCompare(b.date));
}

/** "Ta in naslednji"/"Vse" for a move to another weekday: rewrite BYDAY of a weekly rule. */
export function remapWeekday(rrule: string, fromDate: LocalDate, toDate: LocalDate): string | null {
  const parts = rrule.replace(/^RRULE:/i, '').split(';');
  if (!parts.includes('FREQ=WEEKLY')) return null;
  const codes = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
  const from = codes[weekdayOf(fromDate) - 1] ?? '';
  const to = codes[weekdayOf(toDate) - 1] ?? '';
  const byday = parts.find((p) => p.startsWith('BYDAY='));
  const days = byday ? byday.slice(6).split(',') : [from];
  if (!days.includes(from) || days.includes(to)) return null;
  const next = days
    .map((d) => (d === from ? to : d))
    .sort((a, b) => codes.indexOf(a) - codes.indexOf(b));
  const out = parts.filter((p) => !p.startsWith('BYDAY='));
  out.push(`BYDAY=${next.join(',')}`);
  return out.join(';');
}
